import { Observable, of } from 'rxjs';
import { map, shareReplay, switchMap, catchError } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { AgencyModel } from '../../models/agency.model';
import { CurrentContextService } from '../current-context/current-context.service';
import { FlipperFlags } from './flipper-flags';

type EnabledFlippers = { isEnabled: (feature: FlipperFlags) => boolean };

@Injectable({
  providedIn: 'root',
})
export class FlipperService {
  public flipper$: Observable<EnabledFlippers> = this.context.eagerFlippers
    ? of(this.context.eagerFlippers).pipe(
        map((enabledFlippers: string[]) =>
          this.enabledFlippers(enabledFlippers)
        ),
        shareReplay(1)
      )
    : this.context.agency.pipe(
        // Call for a new set of flipper enabled features whenever
        // agency changes. Might come in handy if we ever do rolling
        // rollout of features to specific agencies.
        switchMap((agency: AgencyModel) => this.loadFlippers(agency)),
        map((enabledFlippers: string[]) =>
          this.enabledFlippers(enabledFlippers)
        ),
        shareReplay(1)
      );

  public zuoraMaintenance$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('zuora_maintenance'))
  );

  public reportingNoop$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('reporting_noop'))
  );

  public acceleratedCallLog$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('accelerated_call_log'))
  );

  public otherHomepage$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('other_homepage'))
  );

  public fullstory$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('allow_fullstory_tracking'))
  );

  public cursorPaginateAcceleratedCallLog$: Observable<boolean> =
    this.flipper$.pipe(
      map((feature) =>
        feature.isEnabled('cursor_paginate_accelerated_call_log')
      )
    );

  public createNotificationRedesign$: Observable<boolean> = this.flipper$.pipe(
    map((feature) => feature.isEnabled('create_notification_redesign'))
  );

  constructor(
    private http: HttpClient,
    private context: CurrentContextService
  ) {}

  public flipperEnabled(flipper_name: FlipperFlags): Observable<boolean> {
    return this.flipper$.pipe(
      map((feature) => feature.isEnabled(flipper_name))
    );
  }

  public flipperEnabledForParentAccount(
    flipper_name: FlipperFlags,
    parentAccountId: string
  ): Observable<boolean> {
    return this.http
      .get<{
        enabled: boolean;
      }>(
        `/superadmin/parent_accounts/${parentAccountId}/flipper_status.json?flipper=${flipper_name}`
      )
      .pipe(
        map((response) => response.enabled),
        catchError(() => of(false))
      );
  }

  public eagerlyEnabled(name: FlipperFlags): boolean {
    return (this.context.eagerFlippers || []).includes(name);
  }

  private loadFlippers(agency: AgencyModel): Observable<FlipperFlags[]> {
    // for billing and and invite, there is no agency yet.
    // these features are apart of signup.
    let url = '/flipper/enabled.json';

    if (agency && agency.id) {
      url = `/flipper/enabled.json?agency_id=${agency.id}`;
    }

    return this.http.get<FlipperFlags[]>(url);
  }

  private enabledFlippers(enabledFlippers): EnabledFlippers {
    return {
      isEnabled(feature: string): boolean {
        return enabledFlippers.includes(feature);
      },
    };
  }
}
